import * as Y from "yjs"
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from "y-protocols/awareness"
import { createConsumer } from "@rails/actioncable"

// Yjs over ActionCable: a provider for one shared document (a CollabChannel room). The server only stores and relays
// the bytes (base64 on the wire); the merging, the cursors and the "who is here" all happen here, with Yjs.
//
//   const provider = new CableProvider(ydoc, { kind: "document", id: 42, user: { name, color } })
//   const state = await provider.ready          // { empty, writable } once the room's state has been applied
//   if (state.empty) await provider.seed(Y.encodeStateAsUpdate(startingDoc))
//   provider.awareness                          // for the cursor extension
//   provider.destroy()
//
// Remote changes are applied with origin REMOTE and never sent back; everything else on the doc is sent.
const REMOTE = "cable"
const COMPACT_ABOVE = 150

let sharedConsumer
const consumer = () => (sharedConsumer ||= createConsumer())

const toBase64 = (bytes) => {
    let binary = ""
    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
    return btoa(binary)
}
const fromBase64 = (text) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0))

export class CableProvider {
    constructor(doc, { kind, id, user, onReset, onStatus }) {
        this.doc = doc
        this.onReset = onReset
        this.onStatus = onStatus
        this.awareness = new Awareness(doc)
        this.awareness.setLocalStateField("user", user)
        this.writable = false
        this.lastId = null
        this.epoch = null
        this.joined = false
        this._seedWaiter = null
        this.ready = new Promise((resolve) => { this._resolveReady = resolve })

        this._onDocUpdate = (update, origin) => {
            if (origin === REMOTE || origin === "seed" || !this.writable) return

            this._send("update", { update: toBase64(update) })
        }
        this._onAwareness = ({ added, updated, removed }, origin) => {
            if (origin === REMOTE) return

            this._send("awareness", { update: toBase64(encodeAwarenessUpdate(this.awareness, [...added, ...updated, ...removed])) })
        }
        doc.on("update", this._onDocUpdate)
        this.awareness.on("update", this._onAwareness)

        this.subscription = consumer().subscriptions.create(
            { channel: "CollabChannel", kind, id, client_id: doc.clientID },
            {
                connected: () => this.onStatus?.("connected"),
                disconnected: () => this.onStatus?.("disconnected"),
                rejected: () => this._resolveReady(null),
                received: (message) => this._received(message)
            }
        )
    }

    // The room had no state and this client's copy of the document goes in as it. Resolves true when it won (the
    // update is applied here too), false when someone else seeded first (their state is applied instead).
    seed(update) {
        return new Promise((resolve) => {
            this._seedWaiter = (won) => {
                if (won) Y.applyUpdate(this.doc, update, "seed")
                resolve(won)
            }
            this._send("seed", { update: toBase64(update) })
        })
    }

    destroy() {
        this.doc.off("update", this._onDocUpdate)
        this.awareness.setLocalState(null)
        this.awareness.off("update", this._onAwareness)
        this.awareness.destroy()
        this.subscription?.unsubscribe()
    }

    _send(action, data) {
        this.subscription?.perform(action, data)
    }

    _received(message) {
        switch (message.type) {
        case "sync": return this._synced(message)
        case "update":
            Y.applyUpdate(this.doc, fromBase64(message.update), REMOTE)
            this.lastId = Math.max(this.lastId || 0, message.id || 0)
            break
        case "awareness": {
            const known = this.awareness.getStates().has(message.from)
            applyAwarenessUpdate(this.awareness, fromBase64(message.update), REMOTE)
            // Someone new: say who we are, so their screen shows our cursor now rather than at our next heartbeat.
            if (!known && message.from !== this.doc.clientID) setTimeout(() => this._announce(), 50 + Math.random() * 250)
            break
        }
        case "left": removeAwarenessStates(this.awareness, [message.from], REMOTE); break
        case "seeded":
            this.epoch = message.id
            this._finishSeed(true)
            break
        case "reset": this.onReset?.(); break
        }
    }

    _synced({ updates, last_id: lastId, epoch, writable, rows }) {
        // Back after a drop to a room that was thrown away and started again meanwhile: this copy of the document
        // is not part of it, and merging the two would double the text. Start over from the page.
        const seeding = !!this._seedWaiter
        if (this.joined && !seeding && this.epoch !== epoch) {
            this.onReset?.()
            return
        }
        this.epoch = epoch
        updates.forEach((update) => Y.applyUpdate(this.doc, fromBase64(update), REMOTE))
        this.lastId = lastId
        this.writable = writable
        // Waiting to seed and someone else got there first: their state is what we have now.
        if (seeding) this._finishSeed(false)
        if (this.joined && !seeding) {
            // Back after a dropped connection: send anything typed while away (Yjs ignores what the room has).
            if (writable) this._send("update", { update: toBase64(Y.encodeStateAsUpdate(this.doc)) })
        } else if (!this.joined) {
            this.joined = true
            this._resolveReady({ empty: updates.length === 0, writable })
            if (writable && rows > COMPACT_ABOVE) this._send("compact", { update: toBase64(Y.encodeStateAsUpdate(this.doc)), through: lastId })
        }
        this._announce()
    }

    _finishSeed(won) {
        const waiter = this._seedWaiter
        this._seedWaiter = null
        waiter?.(won)
    }

    _announce() {
        this._send("awareness", { update: toBase64(encodeAwarenessUpdate(this.awareness, [this.doc.clientID])) })
    }
}
